"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEmailComposerAction } from "../../analytics";
import { useEmailComposer } from "../../context";
import { useEditorState } from "@tiptap/react";
import { useState } from "react";
import type { IconType } from "react-icons/lib";
import { LuAlignCenter, LuAlignLeft, LuAlignRight } from "react-icons/lu";
import { Button } from "@/components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cx } from "@/lib/tailwind-utils";
const ALIGNMENT_NODES = ["emailImage", "emailButton"] as const;
const ALIGNMENTS: {
  value: "left" | "center" | "right";
  icon: IconType;
  label: string;
}[] = [{
  value: "left",
  icon: LuAlignLeft,
  label: "Align left"
}, {
  value: "center",
  icon: LuAlignCenter,
  label: "Align center"
}, {
  value: "right",
  icon: LuAlignRight,
  label: "Align right"
}];
function useActiveAlignment() {
  const $ = _c(2);
  const {
    editor
  } = useEmailComposer();
  let t0;
  if ($[0] !== editor) {
    t0 = {
      editor,
      selector: _temp
    };
    $[0] = editor;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useEditorState(t0) as "left" | "center" | "right";
}
function _temp(ctx) {
  if (!ctx.editor) {
    return "left";
  }
  for (const node of ALIGNMENT_NODES) {
    if (ctx.editor.isActive(node)) {
      return (ctx.editor.getAttributes(node).alignment as string ?? "center") as "left" | "center" | "right";
    }
  }
  if (ctx.editor.isActive({
    textAlign: "center"
  })) {
    return "center";
  }
  if (ctx.editor.isActive({
    textAlign: "right"
  })) {
    return "right";
  }
  return "left";
}
function applyAlignment(editor: ReturnType<typeof useEmailComposer>["editor"], value: "left" | "center" | "right") {
  if (!editor) return;
  const activeNode = ALIGNMENT_NODES.find(n => editor.isActive(n));
  if (activeNode) {
    editor.chain().focus().updateAttributes(activeNode, {
      alignment: value
    }).run();
  } else {
    editor.chain().focus().setTextAlign(value).run();
  }
  trackEmailComposerAction("align_changed", "toolbar", {
    alignment: value
  });
}
export function AlignButton(t0) {
  const $ = _c(15);
  const {
    value,
    icon: Icon,
    title
  } = t0;
  const {
    editor
  } = useEmailComposer();
  let t1;
  if ($[0] !== value) {
    t1 = ctx => {
      if (!ctx.editor) {
        return false;
      }
      for (const node of ALIGNMENT_NODES) {
        if (ctx.editor.isActive(node)) {
          return (ctx.editor.getAttributes(node).alignment ?? "center") === value;
        }
      }
      return ctx.editor.isActive({
        textAlign: value
      });
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== editor || $[3] !== t1) {
    t2 = {
      editor,
      selector: t1
    };
    $[2] = editor;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isActive = useEditorState(t2);
  let t3;
  if ($[5] !== editor || $[6] !== value) {
    t3 = () => applyAlignment(editor, value);
    $[5] = editor;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isActive ? "bg-hover" : "";
  let t5;
  if ($[8] !== Icon) {
    t5 = <Icon size={16} />;
    $[8] = Icon;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== title) {
    t6 = <Button type="button" variant="ghost" size="icon-sm" onClick={t3} className={t4} tooltip={title}>{t5}</Button>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function AlignDropdown() {
  const $ = _c(16);
  const {
    editor
  } = useEmailComposer();
  const active = useActiveAlignment();
  const [open, setOpen] = useState(false);
  let t0;
  if ($[0] !== active) {
    t0 = ALIGNMENTS.find(a => a.value === active)?.icon ?? LuAlignLeft;
    $[0] = active;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const ActiveIcon = t0;
  let t1;
  if ($[2] !== editor?.commands) {
    t1 = next => {
      setOpen(next);
      if (!next) {
        editor?.commands.focus();
      }
    };
    $[2] = editor?.commands;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== ActiveIcon) {
    t2 = <PopoverTrigger asChild={true}><Button type="button" variant="ghost" size="icon-sm" tooltip="Alignment"><ActiveIcon size={16} /></Button></PopoverTrigger>;
    $[4] = ActiveIcon;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== active || $[7] !== editor) {
    t3 = ALIGNMENTS.map(t4 => {
      const {
        value,
        icon: Icon,
        label
      } = t4;
      return <Button type="button" key={value} variant="ghost" size="icon-sm" className={cx(active === value && "bg-hover")} onMouseDown={_temp4} onClick={() => {
        if (!editor) {
          return;
        }
        const activeNode = ALIGNMENT_NODES.find(n => editor.isActive(n));
        if (activeNode) {
          editor.commands.updateAttributes(activeNode, {
            alignment: value
          });
        } else {
          editor.commands.setTextAlign(value);
        }
        trackEmailComposerAction("align_changed", "toolbar", {
          alignment: value
        });
      }} tooltip={label}><Icon size={16} /></Button>;
    });
    $[6] = active;
    $[7] = editor;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <PopoverContent align="center" sideOffset={8} className="w-fit min-w-0 p-1 focus:outline-none" onOpenAutoFocus={_temp2} onFocusOutside={_temp3}><div className="flex items-center gap-0.5">{t3}</div></PopoverContent>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== open || $[12] !== t1 || $[13] !== t2 || $[14] !== t4) {
    t5 = <Popover open={open} onOpenChange={t1}>{t2}{t4}</Popover>;
    $[11] = open;
    $[12] = t1;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function _temp4(e_1) {
  return e_1.preventDefault();
}
function _temp3(e_0) {
  return e_0.preventDefault();
}
function _temp2(e) {
  return e.preventDefault();
}